/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Component, Inject, OnDestroy, OnInit, signal} from '@angular/core';
import {NgForm} from '@angular/forms';
import {UIRouter} from '@uirouter/core';
import {Subscription} from 'rxjs';
import * as _ from 'lodash';
import {AppEventBusService} from '../../common/events/app-event-bus.service';

import {UpdateSession} from '../../../components/session/update-session';
import {buildElectionFormOptions, buildElectionSelectGroups} from '../../../components/util/election-form-options';
import {
  clearFormValidation,
  createFormSubmitState,
  FormControlError,
  FormSubmitState,
  handleApiValidationErrors,
  hasFormValidationError,
  markFormSubmitError,
  markFormSubmitSuccess,
  updateFormWarnings
} from '../../common/controller/form-submit-state';
import {
  convertToApiBudgets,
  convertToApiEntities,
  convertToUiBudgets,
  convertToUiEntities,
  doTheCopyOfEntity
} from './advertiser-budgets';
import template from './update-advertiser-page.component.html';
import {LocalNetworkProfile} from '../../../components/session/local-network-profile';
import {BrowserLocationService} from '../../angular-platform-providers';
import {compareById} from '../../common/util/object-id-comparison';
import {UpdateAdvertiserApiService} from './update-advertiser-api.service';
import {deferSignalStateWrite} from '../../common/util/template-state';

@Component({
  standalone: false,
  selector: 'adn-update-advertiser-page',
  template
})
export class UpdateAdvertiserPageComponent implements OnInit, OnDestroy {
  readonly entityTypes = ['CORPORATE_ENTITY_PUBLIC', 'CORPORATE_ENTITY_PRIVATE', 'POLITICAL_PARTY', 'INDIVIDUAL'];

  private readonly modelState = signal<any>(undefined);
  private readonly entitiesState = signal<any[]>([]);
  private readonly electionsState = signal<any[]>([]);
  private readonly electionSelectGroupsState = signal<any>({dated: [], undated: [], useGroups: false, groups: []});
  private readonly availableCurrenciesState = signal<string[]>([]);
  private readonly doneState = signal(false);
  private readonly missingAdvertiserState = signal(false);
  private readonly pendingState = signal(false);
  private readonly angularFormDirtyState = signal(false);
  private readonly submitStateRevision = signal(0);

  get model(): any { return this.modelState(); }
  set model(value: any) { this.modelState.set(value); }
  get entities(): any[] { return this.entitiesState(); }
  set entities(value: any[]) { this.entitiesState.set(value); }
  get elections(): any[] { return this.electionsState(); }
  set elections(value: any[]) { this.electionsState.set(value); }
  get electionSelectGroups(): any { return this.electionSelectGroupsState(); }
  set electionSelectGroups(value: any) { this.electionSelectGroupsState.set(value); }
  get availableCurrencies(): string[] { return this.availableCurrenciesState(); }
  set availableCurrencies(value: string[]) { this.availableCurrenciesState.set(value); }
  get done(): boolean { return this.doneState(); }
  set done(value: boolean) { this.doneState.set(value); }
  get missingAdvertiser(): boolean { return this.missingAdvertiserState(); }
  set missingAdvertiser(value: boolean) { this.missingAdvertiserState.set(value); }
  get pending(): boolean { return this.pendingState(); }
  set pending(value: boolean) { this.pendingState.set(value); }
  get registeredForm(): NgForm | null { return this._registeredForm; }
  get validSubmission(): boolean { this.submitStateRevision(); return this.submitState.validSubmission; }
  get apiErrors(): Array<{key: string; apiText: string}> { this.submitStateRevision(); return this.submitState.apiErrors; }
  get clientErrors(): any[] { this.submitStateRevision(); return this.submitState.clientErrors; }
  get hasValidationWarnings(): boolean { this.submitStateRevision(); return this.submitState.hasValidationWarnings; }
  get bulkError(): boolean | undefined { this.submitStateRevision(); return this.submitState.bulkError; }
  get hasValidationError(): boolean { this.submitStateRevision(); return hasFormValidationError(this.submitState); }

  submitState: FormSubmitState = createFormSubmitState();

  private rawElections: any[] = [];
  private savedModel: any;
  private _registeredForm: NgForm | null = null;
  private _formSub: Subscription | null = null;
  private _formSettleTimer: any = null;
  private _warningInitTimer: any = null;
  private _initializingForm = false;
  private readonly formControlErrors: FormControlError[] = [];

  constructor(
    private readonly eventBus: AppEventBusService,
    @Inject(BrowserLocationService) private readonly location: any,
    private readonly updateAdvertiserApi: UpdateAdvertiserApiService,
    @Inject(LocalNetworkProfile) private readonly LocalNetworkProfile: any,
    private readonly uiRouter: UIRouter
  ) {}

  ngOnInit(): void {
    const routeParams = this.uiRouter.globals.params;
    if (routeParams.context) {
      UpdateSession.obtain().init(routeParams.context, routeParams.auth_token);
    }

    this.availableCurrencies = ['DKK', 'EUR', 'NOK', 'SEK'];
    if (this.location.hostname === 'localhost') {
      this.availableCurrencies.push('AUD');
    }

    this.updateAdvertiserApi.listElections().subscribe((data) => {
      deferSignalStateWrite(() => {
        this.rawElections = data.results || [];
        this.refreshElections();
      });
    });

    const advertiserId = routeParams.id || 'missing';
    this.updateAdvertiserApi.getAdvertiser(advertiserId).subscribe({
      next: (model) => {
        deferSignalStateWrite(() => {
          this.done = true;
          this.setUpResourceForm(model);
        });
      },
      error: () => {
        deferSignalStateWrite(() => {
          this.missingAdvertiser = true;
          this.done = true;
        });
      }
    });
  }

  ngOnDestroy(): void {
    this._formSub?.unsubscribe();
    clearTimeout(this._formSettleTimer);
    clearTimeout(this._warningInitTimer);
  }

  registerForm(editForm: NgForm): string | null {
    if (this._registeredForm !== editForm) {
      this._registeredForm = editForm;
      clearTimeout(this._warningInitTimer);
      this._warningInitTimer = setTimeout(() => {
        updateFormWarnings(this.submitState, this.model, this.LocalNetworkProfile.isPureMarketplacePlatform());
        this.notifySubmitState();
      }, 100);
      this._startTrackingForm();
    }
    return null;
  }

  submit(editForm: NgForm): void {
    if (this.pending || this.isSaveDisabled(editForm)) {
      return;
    }

    this.pending = true;
    this.submitState.ngForm = editForm;
    clearFormValidation(this.submitState, this.formControlErrors);
    this.onBeforeSubmit();
    this.updateAdvertiserApi.saveAdvertiser(this.model).subscribe({
      next: (resource) => {
        deferSignalStateWrite(() => {
          updateFormWarnings(this.submitState, resource || this.model, this.LocalNetworkProfile.isPureMarketplacePlatform());
          this.acceptSavedResource(resource);
          markFormSubmitSuccess(this.submitState, editForm);
          this.angularFormDirtyState.set(false);
          this._startTrackingForm();
          this.finishSubmit();
        });
      },
      error: (response) => {
        deferSignalStateWrite(() => {
          if ((response.status === 400 || response.status === 403 || response.status === 422)
            && (response.data.type === 'ValidationResult' || response.data.type === 'ErrorMessage')) {
            handleApiValidationErrors(
              this.submitState,
              this.model,
              _.get(response, ['data', 'errors']) || response.data,
              this.formControlErrors,
              editForm
            );
          }
          markFormSubmitError(this.submitState, editForm);
          this.finishSubmit();
        });
      }
    });
  }

  isFormPristine(editForm: NgForm): boolean {
    void editForm;
    return !this.angularFormDirtyState();
  }

  isSaveDisabled(editForm: NgForm): boolean {
    return this.isFormPristine(editForm)
      || this.pending
      || (editForm.invalid && !hasFormValidationError(this.submitState));
  }

  readonly compareById = compareById;

  trackByIndex(index: number): number {
    return index;
  }

  copyToEntity(entity: any): void {
    this.editsMade();
    doTheCopyOfEntity(this.model, entity);
  }

  addElectionBudget(): void {
    this.editsMade();
    this.model.uiPoliticalBudgets = this.model.uiPoliticalBudgets || [];
    this.model.uiPoliticalBudgets.push({});
  }

  removeElection(index: number): void {
    this.editsMade();
    _.pullAt(this.model.uiPoliticalBudgets, index);
  }

  removeCampaign(budget: any, index: number): void {
    this.editsMade();
    _.pullAt(budget.campaigns, index);
  }

  addCampaign(budget: any): void {
    this.editsMade();
    budget.campaigns = budget.campaigns || [];
    budget.campaigns.push({
      campaignName: '',
      budget: {}
    });
  }

  editsMade(): void {
    if (this._initializingForm) {
      return;
    }
    this._registeredForm?.control.markAsDirty();
    this.angularFormDirtyState.set(true);
  }

  cancel(editForm: NgForm): void {
    if (this.isFormPristine(editForm) || this.pending || (editForm.invalid && !hasFormValidationError(this.submitState))) {
      return;
    }
    this._initializingForm = true;
    this.resetWorkingModel();
    this.submitState.validSubmission = false;
    this.notifySubmitState();
    this.angularFormDirtyState.set(false);
    editForm?.control.markAsPristine();
    this._initializingForm = false;
    this._startTrackingForm();
  }

  onBeforeSubmit(): void {
    this.model.politicalBudgets = convertToApiBudgets(this.model.uiPoliticalBudgets);
    convertToApiEntities(this.model);
  }

  private setUpResourceForm(model: any): void {
    this.savedModel = _.cloneDeep(model);
    this._initializingForm = true;
    this.resetWorkingModel();
    this._initializingForm = false;
  }

  private acceptSavedResource(resource: any): void {
    _.set(this.model, 'validationWarnings', resource.validationWarnings);
    _.set(this.model, 'status', resource.status);
    this.savedModel = _.cloneDeep(this.model);
    this.applyUiModel(resource || this.model);
  }

  private resetWorkingModel(): void {
    this.model = _.cloneDeep(this.savedModel);
    this.model.type = this.model.type || null;
    this.model.address = this.model.address || {};
    this.applyUiModel(this.model);
  }

  private applyUiModel(source: any): void {
    this.model.uiPoliticalBudgets = convertToUiBudgets(source);
    convertToUiEntities(source);
    [this.model.controllingEntity, this.model.payingEntity].forEach((entity: any) => {
      entity.address = entity.address || {};
    });
    this.entities = [this.model.controllingEntity, this.model.payingEntity];
    this.refreshElections();
  }

  private refreshElections(): void {
    this.elections = buildElectionFormOptions(this.rawElections || [], {
      selectedElectionIds: ((this.model && this.model.uiPoliticalBudgets) || []).map((budget: any) => {
        return budget.election && budget.election.id;
      }),
      onlyFuture: true
    });
    this.electionSelectGroups = buildElectionSelectGroups(this.elections);
  }

  private _startTrackingForm(): void {
    this._formSub?.unsubscribe();
    clearTimeout(this._formSettleTimer);
    const form = this._registeredForm;
    if (!form) {
      return;
    }
    this._formSettleTimer = setTimeout(() => {
      this._formSub = form.form?.valueChanges?.subscribe(() => {
        if (!this._initializingForm) {
          this.angularFormDirtyState.set(true);
        }
      }) || null;
    }, 0);
  }

  private finishSubmit(): void {
    this.pending = false;
    this.notifySubmitState();
    this.eventBus.emit('SUBMIT_PROCESSED');
  }

  dismissSaveMessage(): void {
    this.submitState.validSubmission = false;
    this.notifySubmitState();
  }

  private notifySubmitState(): void {
    this.submitStateRevision.update((revision) => revision + 1);
  }
}
